import { trackCityScopeEvent } from './analyticsJourneys';

export const PLANNING_APPLICATION_MODAL_SURFACE = 'planning_application_modal';

export const PLANNING_APPLICATION_MODAL_ACTIONS = {
  opened: 'application_modal_opened',
  tabSelected: 'application_modal_tab_selected',
  tabViewed: 'application_modal_tab_viewed',
  tabDwell: 'application_modal_tab_dwell',
  closed: 'application_modal_closed',
  abandoned: 'application_modal_abandoned',
} as const;

type ModalAnalyticsAction =
  (typeof PLANNING_APPLICATION_MODAL_ACTIONS)[keyof typeof PLANNING_APPLICATION_MODAL_ACTIONS];

export interface PlanningApplicationModalTab {
  id: string;
  label?: string;
}

export interface PlanningApplicationModalAnalyticsSession {
  sessionId: string;
  reference: string;
  openedAtMs: number;
  activeTab: string;
  tabEnteredAtMs: number;
  eventSequence: number;
  viewedTabs: Set<string>;
  selectedTabCount: number;
  closeTracked: boolean;
  availableTabs: PlanningApplicationModalTab[];
  baseMetadata: Record<string, unknown>;
}

interface CreatePlanningApplicationModalAnalyticsSessionInput {
  reference: string;
  activeTab: string;
  availableTabs: PlanningApplicationModalTab[];
  baseMetadata?: Record<string, unknown>;
  nowMs?: number;
}

interface TrackPlanningApplicationModalEventInput {
  tabId?: string;
  previousTabId?: string;
  trigger?: string;
  closeReason?: string;
  durationMs?: number;
  metadata?: Record<string, unknown>;
  name?: string;
}

const FALLBACK_TAB_LABELS: Record<string, string> = {
  overview: 'Overview',
  map: 'Map',
  signals: 'Tags',
  form: 'Application Insights',
  applications: 'Related applications',
  timeline: 'Timeline',
  discussion: 'Discussion',
  journal: 'Journal',
  reports: 'Reports',
  documents: 'Documents',
};

function cleanMetadata(metadata: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(metadata).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  );
}

function createSessionId(reference: string, nowMs: number): string {
  const safeRef = String(reference || 'unknown')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
    .slice(0, 48);
  const suffix = Math.random().toString(36).slice(2, 8);
  return `pam_${safeRef || 'application'}_${nowMs.toString(36)}_${suffix}`;
}

export function modalDurationSeconds(durationMs?: number): number | undefined {
  if (typeof durationMs !== 'number' || !Number.isFinite(durationMs)) return undefined;
  return Math.max(0, Math.round(durationMs / 100) / 10);
}

export function getPlanningApplicationModalTabLabel(
  tabId?: string,
  availableTabs: PlanningApplicationModalTab[] = [],
): string | undefined {
  if (!tabId) return undefined;
  const configuredTab = availableTabs.find((tab) => tab.id === tabId);
  return configuredTab?.label || FALLBACK_TAB_LABELS[tabId] || tabId;
}

export function createPlanningApplicationModalAnalyticsSession(
  input: CreatePlanningApplicationModalAnalyticsSessionInput,
): PlanningApplicationModalAnalyticsSession {
  const nowMs = input.nowMs ?? Date.now();
  const availableTabs = input.availableTabs.length
    ? input.availableTabs
    : [{ id: input.activeTab || 'overview', label: getPlanningApplicationModalTabLabel(input.activeTab) }];
  const activeTab = availableTabs.some((tab) => tab.id === input.activeTab)
    ? input.activeTab
    : availableTabs[0]?.id || 'overview';

  return {
    sessionId: createSessionId(input.reference, nowMs),
    reference: input.reference,
    openedAtMs: nowMs,
    activeTab,
    tabEnteredAtMs: nowMs,
    eventSequence: 0,
    viewedTabs: new Set([activeTab]),
    selectedTabCount: 0,
    closeTracked: false,
    availableTabs,
    baseMetadata: cleanMetadata({
      surface: PLANNING_APPLICATION_MODAL_SURFACE,
      application_reference: input.reference,
      available_tabs: availableTabs.map((tab) => tab.id).join(','),
      available_tab_count: availableTabs.length,
      ...input.baseMetadata,
    }),
  };
}

export function trackPlanningApplicationModalEvent(
  session: PlanningApplicationModalAnalyticsSession,
  action: ModalAnalyticsAction,
  input: TrackPlanningApplicationModalEventInput = {},
): void {
  const tabLabel = getPlanningApplicationModalTabLabel(input.tabId, session.availableTabs);
  const previousTabLabel = getPlanningApplicationModalTabLabel(input.previousTabId, session.availableTabs);
  const durationSeconds = modalDurationSeconds(input.durationMs);
  session.eventSequence += 1;

  trackCityScopeEvent({
    category: 'journey:planning_research',
    action,
    name: input.name || tabLabel || session.reference || action,
    value: durationSeconds,
    metadata: cleanMetadata({
      ...session.baseMetadata,
      session_id: session.sessionId,
      event_sequence: session.eventSequence,
      tab_id: input.tabId,
      tab_label: tabLabel,
      previous_tab_id: input.previousTabId,
      previous_tab_label: previousTabLabel,
      trigger: input.trigger,
      close_reason: input.closeReason,
      duration_ms: typeof input.durationMs === 'number' ? Math.max(0, Math.round(input.durationMs)) : undefined,
      duration_seconds: durationSeconds,
      viewed_tabs: Array.from(session.viewedTabs).join(','),
      viewed_tab_count: session.viewedTabs.size,
      selected_tab_count: session.selectedTabCount,
      ...input.metadata,
    }),
  });
}
